import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "@/redux/hooks";
import { useSnackBar } from "@/utils/useSnackbar";

import { authSelector } from "@/pages/Public/redux/auth.slice";
import { listSelector } from "@/pages/Private/redux/list/list.slice";

import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";
import { useGenerateCopywritingMutation } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.api";
import { copywritingOrchestrationSelector } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";

import { CopywritingOutputFormat } from "@/pages/Private/pages/Messages/components/GenerateBar/types";
import { useCopywritingSocket } from "@/pages/Private/pages/Messages/components/GenerateBar/useCopywritingSocket";

import { resolveDefaultGenerateSender } from "./resolveDefaultGenerateSender.util";

interface GenerateDraftsForLeadParams {
	inboxIndex: number;
	triggerDescription?: string | null;
}

/**
 * Fires AI draft generation directly (no GenerateBar modal) for a lead in the
 * signal feed, and keeps the copywriting socket alive at page level so live
 * progress (RUNNING → COMPLETED/FAILED) is reflected on the lead even while the
 * user stays on the Feed tab.
 *
 * Must be mounted at the page top level (not inside a tab-gated component) so the
 * socket survives Feed ↔ Chat navigation.
 */
export const useFeedDraftGeneration = () => {
	const { t } = useTranslation();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { inbox, currentLeadIndex } = useAppSelector(listSelector);
	const { user } = useAppSelector(authSelector);
	const companyId = user?.company?.id;

	const { isGenerating, alreadyInProgress, frameworkOverrides, freshStartOverride } =
		useAppSelector(copywritingOrchestrationSelector);

	const { data: sendingUsers } = useGetSendersUsersQuery();
	const [generateCopywriting] = useGenerateCopywritingMutation();

	// Keep the socket bound to the currently-selected prospect and open while a
	// generation is in flight, so completion updates the lead live on the feed.
	useCopywritingSocket({
		companyId,
		prospectId: inbox[currentLeadIndex]?.prospect?.id,
		enabled: isGenerating || alreadyInProgress,
	});

	const generateDraftsForLead = useCallback(
		async ({ inboxIndex, triggerDescription }: GenerateDraftsForLeadParams) => {
			// Read the lead by explicit inboxIndex — never currentLeadIndex, which is
			// stale in this closure right after the feed dispatches the lead selection.
			const lead = inbox[inboxIndex];
			const prospectId = lead?.prospect?.id;
			const listId = lead?.list?.id;

			if (!prospectId || !listId) {
				return;
			}

			const sender = resolveDefaultGenerateSender(sendingUsers?.data, user?.id, {
				prospectHasEmail: !!lead?.prospect?.email,
				prospectHasLinkedinUrl: !!lead?.prospect?.linkedinUrl,
			});

			if (!sender) {
				handleOpenSnackbar(t("profileTracker.signalActions.generateNoSender"));

				return;
			}

			try {
				await generateCopywriting({
					prospectId,
					listId,
					channel: sender.channel,
					language: "auto",
					senderUserId: sender.senderUserId,
					senderEmailId: sender.senderEmailId,
					userInstruction: triggerDescription || undefined,
					linkedinInviteNote: sender.linkedinInviteNote,
					outputFormat: CopywritingOutputFormat.FULL_SEQUENCE,
					frameworkOverrides,
					freshStartOverride: freshStartOverride || undefined,
				}).unwrap();
			} catch (error) {
				const status = (error as { status?: number } | undefined)?.status;

				handleOpenSnackbar(
					status === 409
						? t("profileTracker.signalActions.generateAlreadyInProgress")
						: t("profileTracker.signalActions.generateError")
				);
			}
		},
		[
			inbox,
			sendingUsers?.data,
			user?.id,
			frameworkOverrides,
			freshStartOverride,
			generateCopywriting,
			handleOpenSnackbar,
			t,
		]
	);

	return { generateDraftsForLead, renderFeedDraftSnackbar: renderMessageSnackbar };
};
